/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {useContext, useRef, useState} from 'react';
import {AppContext} from "../../misc/appContext";
import {NO_REPLY_FLEX_KEY} from "../../../util/constants";
import {nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import Account from "../../../models/accountModel";
import {createErrorMessage} from "../../../util/utils";
import EmailSettings from "./emailSettings";
import {PermissionCallback} from "../../../util/pageChanger";
import PageChangePermission from "../../misc/pageChangePermission";

export default function EmailSettingsController() {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const emailMaxProductNameLength = account?.settings.emailMaxProductNameLength;
    let productLength: string;
    if (emailMaxProductNameLength) productLength = emailMaxProductNameLength.toString();
    else productLength = '';
    const [noReplyTracking, setNoReplyTracking] = useState(account?.getFlex(NO_REPLY_FLEX_KEY) === true);
    const [sendToCanceled, setSendToCanceled] = useState(account?.settings?.sendCanceledInvoices ?? false);
    const [maxProductLength, setMaxProductLength] = useState(productLength);
    const [includeUBLAttachment, setIncludeUBLAttachment] = useState(account?.settings?.includeUBLAttachment ?? false);
    const [loading, setLoading] = useState(false);
    const [changed, setChanged] = useState(false);

    function onChangeSendToCanceled() {
        setSendToCanceled(!sendToCanceled);
        setChanged(true);
    }

    function onChangeNoReplyTracking(useTracking: boolean) {
        setNoReplyTracking(useTracking);
        setChanged(true);
    }

    function onChangeMaxProductLength(productLength: string) {
        setMaxProductLength(productLength);
        setChanged(true);
    }

    function onChangeIncludeUBLAttachment(value: boolean) {
        setIncludeUBLAttachment(value);
        setChanged(true);
    }

    function onDenyPageChange(id: number | null, callback: PermissionCallback) {
        if (changed)
            onSubmit(() => {
                callback(id, true);
            })();
        else callback(id, true);
    }

    function onSubmit(callback?: VoidFunction) {
        return function() {
            const {account} = context.accountContext;
            if (!account) return;
            setLoading(true);
            account.setFlex(NO_REPLY_FLEX_KEY, noReplyTracking);
            const maxLength = nullify(maxProductLength);
            account.settings.emailMaxProductNameLength = !maxLength ? null : Number(maxLength);
            account.settings.sendCanceledInvoices = sendToCanceled;
            account.settings.includeUBLAttachment = includeUBLAttachment;
            account.update(undefined, undefined, undefined,
                (account: Account, success: boolean, data: ResponseData) => {
                    setLoading(false);
                    if (success) {
                        context.onChangeContext({account});
                        showSnackbar('De instellingen zijn opgeslagen');
                        setChanged(false);
                        callback?.();
                    } else
                        showDialog(
                            createErrorMessage(data.message ?? 'Het opslaan van de instellingen is niet gelukt.'),
                            'Opslaan mislukt');
                });
        }
    }

    return (
        <>
            <EmailSettings onChangeMaxProductLength={onChangeMaxProductLength}
                           onChangeSendToCanceled={onChangeSendToCanceled}
                           onChangeNoReplyTracking={onChangeNoReplyTracking}
                           onSubmit={onSubmit()} onChangeIncludeUBLAttachment={onChangeIncludeUBLAttachment}
                           noReplyTracking={noReplyTracking} maxProductLength={maxProductLength}
                           loading={loading} sendToCanceled={sendToCanceled}
                           includeUBLAttachment={includeUBLAttachment} changed={changed}/>
            <PageChangePermission changed={changed} onDeny={onDenyPageChange}
                                  denyText='Ja, wijzigingen opslaan' allowText='Nee, pagina verlaten'
                                  allowSecondary={true}>
                De wijzigingen gaan verloren als je de pagina verlaat. Wil je de wijzigingen eerst opslaan?
            </PageChangePermission>
        </>
    )
}